import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { INGREDIENTS } from "../lib/ingredients";

export const IngredientsShowcase = () => {
  const [activeId, setActiveId] = useState(INGREDIENTS[0].id);
  const active = INGREDIENTS.find((i) => i.id === activeId) ?? INGREDIENTS[0];

  return (
    <section data-testid="ingredients-section" className="relative overflow-hidden bg-nosh-cream py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="mb-14 max-w-2xl">
          <div className="mb-2 font-script text-3xl text-nosh-gold">The pantry</div>
          <h2 className="font-display text-4xl font-bold leading-tight text-nosh-ink sm:text-5xl">
            Feel the ingredients
          </h2>
          <p className="mt-5 font-body text-lg text-nosh-ink/65">
            Every dish begins here. Hover or tap a spice or herb to discover the little secrets our
            kitchen lives by.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
          {/* Detail panel */}
          <div
            data-testid="ingredient-detail"
            className="relative overflow-hidden rounded-[2rem] bg-nosh-green p-8 shadow-xl md:p-10"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="relative mx-auto h-44 w-44 overflow-hidden rounded-full border-4 border-nosh-cream/85 shadow-2xl md:h-52 md:w-52">
                  <img src={active.image} alt={active.name} className="h-full w-full object-cover" />
                </div>
                <div className="mt-8 text-center">
                  <div className="font-poster text-xs font-bold uppercase tracking-[0.3em] text-nosh-gold">
                    {active.urdu}
                  </div>
                  <h3 className="mt-1 font-display text-4xl font-bold text-nosh-cream">{active.name}</h3>
                  <p className="mx-auto mt-4 max-w-md font-body text-base leading-relaxed text-nosh-cream/85">
                    {active.fact}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {INGREDIENTS.map((ing, idx) => {
              const isActive = ing.id === activeId;
              return (
                <motion.button
                  key={ing.id}
                  data-testid={`ingredient-${ing.id}`}
                  onMouseEnter={() => setActiveId(ing.id)}
                  onFocus={() => setActiveId(ing.id)}
                  onClick={() => setActiveId(ing.id)}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  whileHover={{ y: -6 }}
                  whileTap={{ scale: 0.96 }}
                  className={`group relative flex aspect-square flex-col items-start justify-end overflow-hidden rounded-2xl border-2 p-3 text-left transition-colors ${
                    isActive ? "border-nosh-gold" : "border-nosh-border hover:border-nosh-green"
                  }`}
                >
                  <img
                    src={ing.image}
                    alt={ing.name}
                    className={`absolute inset-0 h-full w-full object-cover transition-transform duration-500 ${
                      isActive ? "scale-110" : "scale-100 group-hover:scale-105"
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-nosh-ink/85 via-nosh-ink/10 to-transparent" />
                  <div className="relative z-10">
                    <div className="font-poster text-[9px] font-bold uppercase tracking-widest text-nosh-gold">
                      {ing.urdu}
                    </div>
                    <div className="font-display text-sm font-bold leading-tight text-nosh-cream">{ing.name}</div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IngredientsShowcase;
